import React, { memo, useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft (pending)" },
  { value: "processed", label: "Processed" },
  { value: "approved", label: "Approved" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "reversed", label: "Reversed" },
  { value: "cancelled", label: "Cancelled" },
];

const PRORATA_BASIS_OPTIONS = [
  { value: "working_days", label: "Working days" },
  { value: "calendar_days", label: "Calendar days" },
  { value: "month_30_days", label: "Month (30 days)" },
];

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const mdl = {
  font: fontStack,
  pageBg: "#ffffff",
  surface: "#ffffff",
  surfaceMuted: "#f8fafc",
  surfaceCard: "#ffffff",
  text: "#0f172a",
  textSoft: "#334155",
  muted: "#64748b",
  accent: COLORS.primary_color_shade6 || "#0288d1",
  accentSoft: COLORS.primary_color_shade5 || "#29b6f6",
  border: "rgba(148, 163, 184, 0.35)",
  borderHairline: "rgba(148, 163, 184, 0.14)",
  shadowCard: "0 1px 2px rgba(15, 23, 42, 0.05), 0 8px 24px rgba(15, 23, 42, 0.06)",
  shadowFloat: "0 20px 50px rgba(15, 23, 42, 0.12)",
  radiusXl: 20,
  radiusLg: 14,
  radiusMd: 10,
  radiusSm: 8,
  ringFocus: "rgba(3, 169, 244, 0.22)",
  footerBg: "#ffffff",
};

/** Readable type scale for modal body and chips */
const payrollType = {
  sectionTitle: 12,
  row: 13,
  chipLabel: 10,
  chipValue: 14,
  fieldLabel: 12,
  fieldLabelCompact: 11,
  fieldInput: 14,
  fieldInputCompact: 13,
  headerLabel: 11,
  headerValue: 15,
};

/** Distinct tint per chip/field so each entity is easy to scan */
const ENTITY_SURFACES = {
  name: {
    bg: "linear-gradient(160deg, rgba(239,246,255,0.98) 0%, rgba(219,234,254,0.5) 100%)",
    border: "rgba(59, 130, 246, 0.4)",
    label: "#1d4ed8",
  },
  staffId: {
    bg: "linear-gradient(160deg, rgba(245,243,255,0.98) 0%, rgba(237,233,254,0.55) 100%)",
    border: "rgba(124, 58, 237, 0.38)",
    label: "#6d28d9",
  },
  salaryId: {
    bg: "linear-gradient(160deg, rgba(255,247,237,0.98) 0%, rgba(254,243,199,0.55) 100%)",
    border: "rgba(217, 119, 6, 0.38)",
    label: "#b45309",
  },
  payMonth: {
    bg: "linear-gradient(160deg, rgba(240,253,250,0.98) 0%, rgba(204,251,241,0.45) 100%)",
    border: "rgba(13, 148, 136, 0.38)",
    label: "#0f766e",
  },
  basic: {
    bg: "linear-gradient(160deg, rgba(238,242,255,0.98) 0%, rgba(224,231,255,0.5) 100%)",
    border: "rgba(79, 70, 229, 0.38)",
    label: "#4338ca",
  },
  payrollStatus: {
    bg: "linear-gradient(160deg, rgba(240,249,255,0.98) 0%, rgba(224,242,254,0.55) 100%)",
    border: "rgba(2, 132, 199, 0.4)",
    label: "#0369a1",
  },
  lock: {
    bg: "linear-gradient(165deg, rgba(254, 252, 232, 0.98) 0%, rgba(254, 243, 199, 0.55) 45%, rgba(253, 230, 138, 0.35) 100%)",
    border: "rgba(217, 119, 6, 0.42)",
    label: "#b45309",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.75), 0 0 0 1px rgba(245, 158, 11, 0.12), 0 4px 14px rgba(180, 83, 9, 0.08)",
  },
  designation: {
    bg: "linear-gradient(160deg, rgba(236,254,255,0.95) 0%, rgba(207,250,254,0.45) 100%)",
    border: "rgba(8, 145, 178, 0.38)",
    label: "#0e7490",
  },
  department: {
    bg: "linear-gradient(160deg, rgba(250,245,255,0.98) 0%, rgba(243,232,255,0.5) 100%)",
    border: "rgba(147, 51, 234, 0.35)",
    label: "#7e22ce",
  },
  salaryType: {
    bg: "linear-gradient(160deg, rgba(255,247,237,0.98) 0%, rgba(255,237,213,0.55) 100%)",
    border: "rgba(234, 88, 12, 0.38)",
    label: "#c2410c",
  },
  joiningDate: {
    bg: "linear-gradient(160deg, rgba(236,253,245,0.98) 0%, rgba(209,250,229,0.45) 100%)",
    border: "rgba(22, 163, 74, 0.35)",
    label: "#15803d",
  },
  statusView: {
    bg: "linear-gradient(160deg, rgba(239,246,255,0.98) 0%, rgba(191,219,254,0.4) 100%)",
    border: "rgba(37, 99, 235, 0.35)",
    label: "#1d4ed8",
  },
  paymentDate: {
    bg: "linear-gradient(160deg, rgba(255,251,235,0.98) 0%, rgba(254,243,199,0.5) 100%)",
    border: "rgba(202, 138, 4, 0.4)",
    label: "#a16207",
  },
  lockedView: {
    bg: "linear-gradient(160deg, rgba(254,252,232,0.98) 0%, rgba(253,230,138,0.35) 100%)",
    border: "rgba(217, 119, 6, 0.4)",
    label: "#b45309",
  },
  remarksView: {
    bg: "linear-gradient(160deg, rgba(250,245,255,0.98) 0%, rgba(237,233,254,0.5) 100%)",
    border: "rgba(139, 92, 246, 0.35)",
    label: "#6d28d9",
  },
  gross: {
    bg: "linear-gradient(160deg, rgba(236,253,245,0.98) 0%, rgba(209,250,229,0.5) 100%)",
    border: "rgba(5, 150, 105, 0.38)",
    label: "#047857",
  },
  allowances: {
    bg: "linear-gradient(160deg, rgba(240,253,250,0.98) 0%, rgba(167,243,208,0.45) 100%)",
    border: "rgba(20, 184, 166, 0.38)",
    label: "#0f766e",
  },
  deductions: {
    bg: "linear-gradient(160deg, rgba(253,242,248,0.98) 0%, rgba(251,207,232,0.45) 100%)",
    border: "rgba(219, 39, 119, 0.36)",
    label: "#be185d",
  },
  netSalary: {
    bg: "linear-gradient(160deg, rgba(224,231,255,0.98) 0%, rgba(199,210,254,0.5) 100%)",
    border: "rgba(67, 56, 202, 0.4)",
    label: "#4338ca",
  },
  roundedNet: {
    bg: "linear-gradient(160deg, rgba(241,245,249,0.98) 0%, rgba(226,232,240,0.55) 100%)",
    border: "rgba(100, 116, 139, 0.4)",
    label: "#475569",
  },
  workingDays: {
    bg: "linear-gradient(160deg, rgba(248,250,252,0.98) 0%, rgba(226,232,240,0.55) 100%)",
    border: "rgba(71, 85, 105, 0.55)",
    label: "#475569",
    value: "#0f172a",
  },
  payableDays: {
    bg: "linear-gradient(160deg, rgba(241,245,249,0.98) 0%, rgba(203,213,225,0.45) 100%)",
    border: "rgba(100, 116, 139, 0.55)",
    label: "#64748b",
    value: "#1e293b",
  },
  present: {
    bg: "linear-gradient(160deg, rgba(240,253,244,0.98) 0%, rgba(187,247,208,0.45) 100%)",
    border: "rgba(34, 197, 94, 0.55)",
    label: "#15803d",
    value: "#14532d",
  },
  absent: {
    bg: "linear-gradient(160deg, rgba(254,242,242,0.98) 0%, rgba(254,202,202,0.45) 100%)",
    border: "rgba(239, 68, 68, 0.55)",
    label: "#b91c1c",
    value: "#7f1d1d",
  },
  leave: {
    bg: "linear-gradient(160deg, rgba(255,247,237,0.98) 0%, rgba(254,215,170,0.45) 100%)",
    border: "rgba(234, 88, 12, 0.55)",
    label: "#c2410c",
    value: "#9a3412",
  },
  paidLeave: {
    bg: "linear-gradient(160deg, rgba(250,245,255,0.98) 0%, rgba(233,213,255,0.45) 100%)",
    border: "rgba(147, 51, 234, 0.52)",
    label: "#7e22ce",
    value: "#581c87",
  },
  halfDay: {
    bg: "linear-gradient(160deg, rgba(254,252,232,0.98) 0%, rgba(253,224,71,0.35) 100%)",
    border: "rgba(202, 138, 4, 0.55)",
    label: "#a16207",
    value: "#854d0e",
  },
  late: {
    bg: "linear-gradient(160deg, rgba(255,241,242,0.98) 0%, rgba(254,205,211,0.45) 100%)",
    border: "rgba(225, 29, 72, 0.52)",
    label: "#be123c",
    value: "#9f1239",
  },
  recordId: {
    bg: "linear-gradient(160deg, rgba(241,245,249,0.98) 0%, rgba(226,232,240,0.5) 100%)",
    border: "rgba(71, 85, 105, 0.38)",
    label: "#334155",
  },
  auditDate: {
    bg: "linear-gradient(160deg, rgba(240,249,255,0.98) 0%, rgba(224,242,254,0.45) 100%)",
    border: "rgba(14, 165, 233, 0.38)",
    label: "#0369a1",
  },
  auditUser: {
    bg: "linear-gradient(160deg, rgba(255,251,235,0.98) 0%, rgba(254,243,199,0.4) 100%)",
    border: "rgba(180, 83, 9, 0.35)",
    label: "#92400e",
  },
  remarks: {
    bg: "linear-gradient(160deg, rgba(250,245,255,0.98) 0%, rgba(237,233,254,0.55) 100%)",
    border: "rgba(139, 92, 246, 0.38)",
    label: "#6d28d9",
  },
};

function entityBoxStyle(toneKey) {
  const e = ENTITY_SURFACES[toneKey];
  if (!e) {
    return {
      background: mdl.surfaceMuted,
      border: `1px solid ${mdl.borderHairline}`,
      boxShadow: undefined,
    };
  }
  return {
    background: e.bg,
    border: `1px solid ${e.border}`,
    boxShadow: e.boxShadow,
  };
}

/** Section body chrome for Days & attendance (view + edit). */
const daysAttendanceSectionBody = {
  background:
    "linear-gradient(168deg, rgba(219, 234, 254, 0.55) 0%, rgba(224, 242, 254, 0.88) 42%, rgba(248, 250, 252, 0.98) 100%)",
  border: `1px solid rgba(2, 132, 199, 0.22)`,
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.92), 0 2px 10px rgba(2, 132, 199, 0.08)",
};

const DAYS_ATTENDANCE_FIELDS = [
  { label: "Working days", tone: "workingDays", key: "working_days" },
  { label: "Payable days", tone: "payableDays", key: "payable_days" },
  { label: "Present", tone: "present", key: "staff_present" },
  { label: "Absent", tone: "absent", key: "staff_absent" },
  { label: "Leave", tone: "leave", key: "staff_leave" },
  { label: "Paid leave", tone: "paidLeave", key: "paid_leave" },
  { label: "Half day", tone: "halfDay", key: "half_day" },
  { label: "Late", tone: "late", key: "late_day" },
];

const PAYROLL_ATTENDANCE_FIELD_KEYS = DAYS_ATTENDANCE_FIELDS.map((f) => f.key);

function daysAttendanceColors(tone) {
  const t = ENTITY_SURFACES[tone];
  if (!t) {
    return { label: mdl.muted, value: mdl.text, border: mdl.borderHairline };
  }
  return {
    label: t.label,
    value: t.value ?? t.label,
    border: t.border,
  };
}

const DaysAttendanceStatChip = memo(function DaysAttendanceStatChip({ label, value, tone }) {
  const c = daysAttendanceColors(tone);
  return (
    <div
      className="h-100 px-2 py-2"
      style={{
        borderRadius: mdl.radiusMd,
        ...entityBoxStyle(tone),
        border: `1.5px solid ${c.border}`,
      }}
    >
      <div
        className="mb-0"
        style={{
          color: c.label,
          fontWeight: 700,
          fontSize: payrollType.chipLabel,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={label}
      >
        {label}
      </div>
      <div
        style={{
          color: c.value,
          fontWeight: 800,
          fontSize: payrollType.chipValue,
          lineHeight: 1.2,
          fontVariantNumeric: "tabular-nums",
          fontFamily: mdl.font,
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
});

function DaysAttendanceSection({ record, form, isEdit, disabled, onPatch }) {
  const fetchFromStaffAttendance =
    isEdit && form
      ? form.is_fetch_attendance !== false
      : Number(record?.is_fetch_attendance) !== 0;

  const editHintStyle = useMemo(
    () => ({
      fontSize: payrollType.fieldLabel,
      color: fetchFromStaffAttendance ? mdl.accent : mdl.muted,
      margin: 0,
    }),
    [fetchFromStaffAttendance]
  );

  const viewChips = useMemo(() => {
    if (isEdit && form) return null;
    return DAYS_ATTENDANCE_FIELDS.map((item) => (
      <div className="col" key={item.key}>
        <DaysAttendanceStatChip
          label={item.label}
          value={formatNum(record[item.key])}
          tone={item.tone}
        />
      </div>
    ));
  }, [isEdit, form, record]);

  return (
    <Section title="Days & attendance" compact variant="daysAttendance">
      {isEdit && form ? (
        <p className="mb-2" style={editHintStyle}>
          {fetchFromStaffAttendance
            ? "Attendance matches staff attendance (seed or Sync Attendance). Save changes to store in payroll. Editing any day field switches to manual entry."
            : "Manual attendance — use Sync Attendance, then Save changes to store in payroll."}
        </p>
      ) : null}
      <div className="row g-2 row-cols-2 row-cols-sm-4 row-cols-lg-8 align-items-stretch">
        {isEdit && form
          ? DAYS_ATTENDANCE_FIELDS.map((item) => (
              <div className="col" key={item.key}>
                <FieldNum
                  label={item.label}
                  value={form[item.key]}
                  onChange={(v) => onPatch(item.key, v)}
                  disabled={disabled}
                  compact
                  entityTone={item.tone}
                  step="1"
                />
              </div>
            ))
          : viewChips}
      </div>
    </Section>
  );
}

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numeric);
}

function formatYearMonth(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length >= 2) {
    const y = parts[0];
    const m = parseInt(parts[1], 10);
    if (m >= 1 && m <= 12) return `${MONTH_SHORT[m - 1]} ${y}`;
  }
  return String(value);
}

function formatDateDMY(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length === 3) {
    const [y, mo, d] = parts;
    return `${d}/${mo}/${y}`;
  }
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("en-IN");
  return String(value);
}

function formatNum(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-IN");
}

function numOrZero(v) {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function normalizeProrataBasis(v) {
  const s = v != null ? String(v).trim() : "";
  return PRORATA_BASIS_OPTIONS.some((o) => o.value === s) ? s : "calendar_days";
}

/** Calendar days in pay month from `year_month` (YYYY-MM). */
function calendarDaysInPayMonth(yearMonth) {
  if (!yearMonth) return 30;
  const parts = String(yearMonth).split("-");
  if (parts.length < 2) return 30;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || m < 1 || m > 12) return 30;
  return new Date(y, m, 0).getDate();
}

/** Days used to derive per-day rate from master basic per prorata basis. */
function resolveProrataDivisorDays(prorataBasis, record, workingDays) {
  switch (prorataBasis) {
    case "month_30_days":
      return 30;
    case "calendar_days":
      return calendarDaysInPayMonth(record.year_month);
    case "working_days":
      return workingDays > 0 ? workingDays : 0;
    default:
      return calendarDaysInPayMonth(record.year_month);
  }
}

function buildFormFromRecord(r) {
  const weekOff = r.week_off_days ?? r.week_off ?? 0;
  const statusVal =
    r.status_code && String(r.status_code).trim() !== ""
      ? String(r.status_code).trim()
      : "draft";
  const storedDeductions = numOrZero(r.total_deductions ?? r.deductions);
  const lopAtLoad = computeLopAmount(r, null, false);
  const otherDeductions = Math.max(0, Math.round((storedDeductions - lopAtLoad) * 100) / 100);
  return {
    gross_earnings: numOrZero(r.gross_earnings ?? r.basic_salary),
    total_deductions: otherDeductions,
    working_days: numOrZero(r.working_days),
    payable_days: numOrZero(r.payable_days),
    staff_present: numOrZero(r.staff_present),
    staff_absent: numOrZero(r.staff_absent),
    staff_leave: numOrZero(r.staff_leave),
    paid_leave: numOrZero(r.paid_leave),
    half_day: numOrZero(r.half_day),
    late_day: numOrZero(r.late_day),
    week_off: numOrZero(weekOff),
    holiday: numOrZero(r.holiday),
    status: STATUS_OPTIONS.some((o) => o.value === statusVal) ? statusVal : "draft",
    remarks: r.remarks != null ? String(r.remarks) : "",
    is_locked: Number(r.is_locked) === 1,
    is_fetch_attendance:
      r.is_fetch_attendance === undefined ||
      r.is_fetch_attendance === null ||
      Number(r.is_fetch_attendance) === 1,
    prorata_basis: normalizeProrataBasis(r.salary_rules?.prorata_basis),
  };
}

function Row({ label, value, entityTone }) {
  const e = entityTone ? ENTITY_SURFACES[entityTone] : null;
  return (
    <div
      className="payroll-mdl-row d-flex justify-content-between align-items-start gap-2 py-1 px-1"
      style={{
        fontSize: payrollType.row,
        fontFamily: mdl.font,
        ...(e
          ? {
              background: e.bg,
              borderRadius: 8,
              border: `1px solid ${e.border}`,
              marginBottom: 4,
              padding: "5px 8px",
            }
          : {
              borderBottom: `1px solid ${mdl.borderHairline}`,
            }),
      }}
    >
      <span style={{ color: e?.label ?? mdl.muted, flexShrink: 0, fontWeight: 600 }}>{label}</span>
      <span
        style={{
          fontWeight: 700,
          color: mdl.text,
          textAlign: "right",
          wordBreak: "break-word",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function formatRuleYesNo(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v) === 1 ? "Yes" : "No";
}

function humanizeRuleEnum(s) {
  if (s == null || s === "") return "—";
  const t = String(s).replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const headerMetaGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
  gap: 10,
  width: "100%",
};

const HEADER_CHIP_THEMES = {
  hero: {
    chip: {
      background: "rgba(255,255,255,0.14)",
      border: "1px solid rgba(255,255,255,0.28)",
      boxShadow: "none",
    },
    label: { color: "rgba(255,255,255,0.92)" },
    value: { color: "#ffffff" },
  },
  surface: {
    chip: {
      background: "#ffffff",
      border: "1px solid rgba(148, 163, 184, 0.28)",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
    },
    label: { color: "#64748b" },
    value: { color: "#0f172a" },
  },
};

function headerChipTheme(variant) {
  return HEADER_CHIP_THEMES[variant] || HEADER_CHIP_THEMES.hero;
}

function payrollHeaderChipBandStyle() {
  return {
    background: `linear-gradient(115deg, ${COLORS.primary_color_shade8 || "#0277BD"} 0%, ${COLORS.primary_color_shade7 || "#01579B"} 28%, ${COLORS.primary_color_shade6 || "#0288d1"} 55%, ${COLORS.primary_color_shade5 || "#29b6f6"} 100%)`,
    color: COLORS.white,
    padding: "20px 24px",
    boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.12)",
  };
}

function PayrollHeaderChipBand({ title, subtitle, children }) {
  return (
    <div className="payroll-header-chip-band" style={payrollHeaderChipBandStyle()}>
      {title ? (
        <h2
          className="payroll-header-chip-band__title"
          style={{
            fontSize: 19,
            fontWeight: 750,
            margin: 0,
            letterSpacing: "-0.02em",
            lineHeight: 1.35,
            textShadow: "0 1px 2px rgba(0,0,0,0.12)",
          }}
        >
          {title}
        </h2>
      ) : null}
      {subtitle ? (
        <p
          className="payroll-header-chip-band__sub"
          style={{
            margin: title ? "6px 0 0" : 0,
            fontSize: 14,
            fontWeight: 600,
            opacity: 0.92,
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </p>
      ) : null}
      <div
        className="payroll-header-chip-band__grid"
        style={{ ...(title || subtitle ? { marginTop: 14 } : null), ...headerMetaGrid }}
      >
        {children}
      </div>
    </div>
  );
}

function StaffPayrollHeaderChip({ label, value, title, variant = "hero" }) {
  const theme = headerChipTheme(variant);
  const isHero = variant === "hero";
  return (
    <div
      style={{
        minWidth: 0,
        width: "100%",
        padding: isHero ? "6px 12px" : "8px 12px",
        borderRadius: isHero ? mdl.radiusSm : mdl.radiusMd,
        boxSizing: "border-box",
        ...theme.chip,
      }}
    >
      <div
        style={{
          fontSize: payrollType.headerLabel,
          fontWeight: isHero ? 600 : 700,
          letterSpacing: isHero ? "0.05em" : "0.06em",
          textTransform: "uppercase",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...theme.label,
        }}
        title={label}
      >
        {label}
      </div>
      <div
        className="text-truncate"
        style={{
          fontWeight: isHero ? 650 : 700,
          fontSize: payrollType.headerValue,
          lineHeight: 1.25,
          fontVariantNumeric: "tabular-nums",
          fontFamily: mdl.font,
          marginTop: isHero ? 0 : 2,
          ...theme.value,
        }}
        title={title != null ? String(title) : value != null ? String(value) : undefined}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

function headerMetaPillFor(variant) {
  return {
    minWidth: 0,
    width: "100%",
    padding: "8px 12px",
    borderRadius: mdl.radiusMd,
    boxSizing: "border-box",
    ...headerChipTheme(variant).chip,
  };
}

function headerMetaLabelFor(variant) {
  return {
    fontSize: payrollType.headerLabel,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 6,
    ...headerChipTheme(variant).label,
  };
}

const headerMetaPill = {
  ...headerMetaPillFor("hero"),
  padding: "6px 12px",
  borderRadius: mdl.radiusSm,
};

const headerMetaLabel = {
  ...headerMetaLabelFor("hero"),
  fontWeight: 600,
  letterSpacing: "0.05em",
  marginBottom: 4,
};

function HeaderLockAction({ isLocked, onLock, onUnlock, busy, saving, variant = "hero" }) {
  const onSurface = variant === "surface";
  const label = isLocked ? "Unlock record" : "Lock record";
  const busyLabel = isLocked
    ? "Unlocking…"
    : saving
      ? "Saving…"
      : "Locking…";
  return (
    <div
      style={{
        ...headerMetaPillFor(variant),
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div style={headerMetaLabelFor(variant)}>Record</div>
      <Button
        variant={
          onSurface
            ? isLocked
              ? "outline-secondary"
              : "warning"
            : isLocked
              ? "outline-light"
              : "warning"
        }
        size="sm"
        onClick={isLocked ? onUnlock : onLock}
        disabled={busy}
        className="w-100 fw-semibold"
        style={{
          borderRadius: mdl.radiusSm,
          fontSize: 13,
          padding: "5px 8px",
          border: !onSurface && isLocked ? "1px solid rgba(255,255,255,0.45)" : undefined,
        }}
      >
        {busy ? busyLabel : label}
      </Button>
    </div>
  );
}

function HeaderSyncAttendanceAction({ onSync, busy, disabled, variant = "hero" }) {
  const onSurface = variant === "surface";
  return (
    <div
      style={{
        ...headerMetaPillFor(variant),
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div style={headerMetaLabelFor(variant)}>Attendance</div>
      <Button
        variant={onSurface ? "outline-primary" : "outline-light"}
        size="sm"
        onClick={onSync}
        disabled={disabled || busy}
        className="w-100 fw-semibold"
        style={{
          borderRadius: mdl.radiusSm,
          fontSize: 13,
          padding: "5px 8px",
          border: onSurface ? undefined : "1px solid rgba(255,255,255,0.45)",
          whiteSpace: "nowrap",
        }}
      >
        {busy ? "Syncing…" : "Sync Attendance"}
      </Button>
    </div>
  );
}

/** Public payroll key for APIs and routes (varchar salary_id, else numeric id). */
export function payRollSalaryApiKey(record) {
  if (!record) return "";
  const code = record.salary_id;
  if (code != null && String(code).trim() !== "") return String(code).trim();
  if (record.id != null && record.id !== "") return String(record.id);
  return "";
}

/** Match a list row to URL/API key (numeric id or varchar salary_id). */
export function payRollSalaryRecordMatches(record, key) {
  if (!record || key == null || key === "") return false;
  const k = String(key);
  if (record.id != null && String(record.id) === k) return true;
  if (record.salary_id != null && String(record.salary_id) === k) return true;
  return false;
}

function staffPayrollViewCells(r) {
  const lockedVal =
    r.is_locked === null || r.is_locked === undefined || r.is_locked === ""
      ? "—"
      : Number(r.is_locked) === 1
        ? "Yes"
        : "No";
  return [
    { label: "Name", value: r.staff_name || "—", tone: "name", title: r.staff_name },
    { label: "Salary ID", value: r.salary_id || r.id || "—", tone: "salaryId" },
    { label: "Staff ID", value: r.staff_id ?? "—", tone: "staffId" },
    { label: "Designation", value: r.designation || "—", tone: "designation", title: r.designation },
    { label: "Department", value: r.department_name || "—", tone: "department", title: r.department_name },
    { label: "Salary type", value: r.salary_type || "—", tone: "salaryType", title: r.salary_type },
    { label: "Joining date", value: formatDateDMY(r.joining_date), tone: "joiningDate" },
    { label: "Pay month", value: formatYearMonth(r.year_month), tone: "payMonth" },
    { label: "Status", value: r.status || r.status_code || "—", tone: "statusView" },
    { label: "Payment date", value: formatDateDMY(r.payment_date), tone: "paymentDate" },
    { label: "Locked", value: lockedVal, tone: "lockedView" },
    { label: "Remarks", value: r.remarks || "—", tone: "remarksView", title: r.remarks },
  ];
}

function HorizontalStatChip({ label, value, tone, centered = false }) {
  const t = ENTITY_SURFACES[tone];
  const labelStyle = {
    color: t?.label || mdl.muted,
    fontWeight: 700,
    fontSize: payrollType.chipLabel,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    lineHeight: 1.25,
    ...(centered
      ? { width: "100%", textAlign: "center" }
      : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
  };
  const valueStyle = {
    color: mdl.text,
    fontWeight: 800,
    fontSize: payrollType.chipValue,
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
    fontFamily: mdl.font,
    ...(centered
      ? { width: "100%", textAlign: "center" }
      : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
  };
  return (
    <div className="col">
      <div
        className="h-100 px-2 py-1"
        style={{
          borderRadius: mdl.radiusMd,
          ...(centered
            ? {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }
            : null),
          ...entityBoxStyle(tone),
        }}
      >
        <div className="mb-0" style={labelStyle} title={label}>
          {label}
        </div>
        <div style={valueStyle} title={value != null ? String(value) : undefined}>
          {value ?? "—"}
        </div>
      </div>
    </div>
  );
}

/** Reference from `tm_payroll_staff_salary_rules` (API: `salary_rules`). Prorata editable in edit mode. */
function SalaryRulesSection({
  rules,
  isEdit,
  prorataValue,
  onProrataChange,
  disabled,
}) {
  if (!rules || rules.id == null || rules.id === "") {
    return (
      <Section title="Salary rules" compact variant="salaryRules">
        <p className="mb-0" style={{ color: mdl.muted, fontFamily: mdl.font, fontSize: payrollType.row }}>
          No linked salary rules row for this staff.
        </p>
      </Section>
    );
  }
  const prorataResolved = normalizeProrataBasis(
    isEdit ? prorataValue : rules.prorata_basis
  );
  const ruleItems = [
    { label: "Week off Paid", value: formatRuleYesNo(rules.is_week_off_paid), tone: "present" },
    { label: "Holiday Paid", value: formatRuleYesNo(rules.is_holiday_paid), tone: "paidLeave" },
    { label: "Half day Weight", value: rules.half_day_weight ?? "—", tone: "halfDay" },
    { label: "Late LOP", value: rules.late_per_lop ?? "—", tone: "absent" },
    { label: "Late min Allowed", value: formatNum(rules.late_minutes_threshold), tone: "recordId" },
  ];

  return (
    <Section title="Salary rules" compact variant="salaryRules">
      <div className="row g-1 row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-6 row-cols-xl-8">
        <div className="col">
          <FieldSelect
            label="Prorata Basis"
            value={prorataResolved}
            options={PRORATA_BASIS_OPTIONS}
            onChange={onProrataChange}
            disabled={!isEdit || disabled}
            entityTone="payMonth"
            centered
          />
        </div>
        {ruleItems.map((item) => (
          <HorizontalStatChip
            key={item.label}
            label={item.label}
            value={item.value}
            tone={item.tone}
            centered
          />
        ))}
      </div>
    </Section>
  );
}

function formatLopDays(value) {
  const n = Math.round(numOrZero(value) * 100) / 100;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}

/** LOP: max(0, Leave − Paid leave) + Absent + late LOP + half day × weight. */
function computePayrollLop(record, form, isEdit) {
  const rules = record.salary_rules;
  const leave = numOrZero(
    isEdit && form ? form.staff_leave : record.staff_leave
  );
  const paidLeave = numOrZero(
    isEdit && form ? form.paid_leave : record.paid_leave
  );
  const absent = numOrZero(
    isEdit && form ? form.staff_absent : record.staff_absent
  );
  const halfDay = numOrZero(isEdit && form ? form.half_day : record.half_day);
  const lateDay = numOrZero(isEdit && form ? form.late_day : record.late_day);

  const unpaidLeave = Math.max(0, leave - paidLeave);
  const leaveAbsentLop = unpaidLeave + absent;

  const latePerLopRaw = rules?.late_per_lop;
  const latePerLop =
    latePerLopRaw != null && latePerLopRaw !== "" && !Number.isNaN(Number(latePerLopRaw))
      ? Math.max(1, numOrZero(latePerLopRaw))
      : 3;
  const lateLop = latePerLop > 0 ? Math.floor(lateDay / latePerLop) : 0;

  const halfDayWeightRaw = rules?.half_day_weight;
  const halfDayWeight =
    halfDayWeightRaw != null &&
    halfDayWeightRaw !== "" &&
    !Number.isNaN(Number(halfDayWeightRaw))
      ? numOrZero(halfDayWeightRaw)
      : 0.5;
  const halfDayLop = Math.round(halfDay * halfDayWeight * 100) / 100;

  const totalLop =
    Math.round((leaveAbsentLop + lateLop + halfDayLop) * 100) / 100;

  return {
    leave,
    paidLeave,
    absent,
    unpaidLeave,
    leaveAbsentLop,
    halfDay,
    halfDayWeight,
    halfDayLop,
    lateDay,
    latePerLop,
    lateLop,
    totalLop,
  };
}

function computePerDayRate(record, form, isEdit) {
  const basic = numOrZero(record.basic_salary);
  const workingDays = numOrZero(
    isEdit && form ? form.working_days : record.working_days
  );
  const prorataBasis = normalizeProrataBasis(
    isEdit && form
      ? form.prorata_basis
      : record.salary_rules?.prorata_basis
  );
  const divisorDays = resolveProrataDivisorDays(
    prorataBasis,
    record,
    workingDays
  );
  const salaryType = (record.salary_type != null
    ? String(record.salary_type).trim()
    : ""
  ).toUpperCase();
  if (basic <= 0) return 0;
  if (salaryType === "DAILY") return basic;
  if (divisorDays <= 0) return 0;
  return Math.round((basic / divisorDays) * 100) / 100;
}

function computeLopAmount(record, form, isEdit) {
  const lop = computePayrollLop(record, form, isEdit);
  const perDayRate = computePerDayRate(record, form, isEdit);
  if (perDayRate <= 0) return 0;
  return Math.round(lop.totalLop * perDayRate * 100) / 100;
}

function computeNetPreview(gross, lopAmount, otherDeductions) {
  const n =
    Math.round(
      (numOrZero(gross) - numOrZero(lopAmount) - numOrZero(otherDeductions)) *
        100
    ) / 100;
  return n < 0 ? 0 : n;
}

/** When payable < working (e.g. mid-month join), scale monthly amounts by payable/working. */
function resolvePayableWorkingProrateFactor(workingDays, payableDays) {
  const w = numOrZero(workingDays);
  const p = numOrZero(payableDays);
  if (w > 0 && p >= 0 && p < w) {
    return p / w;
  }
  return 1;
}

/** Prorated basic pay before salary-component earnings are added. */
function computeCalculatedBaseGross(record, form, isEdit) {
  const basic = numOrZero(record.basic_salary);
  const workingDays = numOrZero(
    isEdit && form ? form.working_days : record.working_days
  );
  const payableDays = numOrZero(
    isEdit && form ? form.payable_days : record.payable_days
  );
  const prorataBasis = normalizeProrataBasis(
    isEdit && form
      ? form.prorata_basis
      : record.salary_rules?.prorata_basis
  );
  const divisorDays = resolveProrataDivisorDays(
    prorataBasis,
    record,
    workingDays
  );
  const salaryType = (
    record.salary_type != null ? String(record.salary_type).trim() : ""
  ).toUpperCase();
  if (basic <= 0) return 0;
  if (salaryType === "DAILY") {
    return Math.round(basic * payableDays * 100) / 100;
  }
  if (
    salaryType === "MONTHLY" &&
    workingDays > 0 &&
    payableDays >= 0 &&
    payableDays < workingDays
  ) {
    return Math.round(basic * (payableDays / workingDays) * 100) / 100;
  }
  if (divisorDays <= 0) return 0;
  return Math.round(basic * (payableDays / divisorDays) * 100) / 100;
}

/**
 * Gross = prorata basic + earning components; other deductions = deduction components sum.
 */
function computePayrollAmounts(record, form, isEdit, salaryComponentsBundle) {
  const workingDays = numOrZero(
    isEdit && form ? form.working_days : record.working_days
  );
  const payableDays = numOrZero(
    isEdit && form ? form.payable_days : record.payable_days
  );
  const { totalEarnings, totalDeductions: componentDeductions } =
    partitionSalaryComponents(salaryComponentsBundle);
  const baseGross = computeCalculatedBaseGross(record, form, isEdit);
  const componentProrateFactor = resolvePayableWorkingProrateFactor(
    workingDays,
    payableDays
  );
  const componentEarningsRaw = Math.round(totalEarnings * 100) / 100;
  const componentEarnings =
    Math.round(componentEarningsRaw * componentProrateFactor * 100) / 100;
  const grossEarnings = Math.round((baseGross + componentEarnings) * 100) / 100;
  const otherDeductions = Math.round(componentDeductions * 100) / 100;
  const lopAmount = computeLopAmount(record, form, isEdit);
  const totalDeductions =
    Math.round((otherDeductions + lopAmount) * 100) / 100;
  const netSalary = computeNetPreview(grossEarnings, lopAmount, otherDeductions);
  return {
    baseGross,
    componentEarnings,
    componentEarningsRaw,
    componentProrateFactor,
    grossEarnings,
    otherDeductions,
    lopAmount,
    totalDeductions,
    netSalary,
  };
}

const PAYMENT_TYPE_OPTIONS = [
  { value: "ADVANCE", label: "Advance" },
  { value: "PART", label: "Partial" },
  { value: "FINAL", label: "Final" },
];

const PAYROLL_PAYMENT_MODE_OPTIONS = [
  { value: "CASH", label: "Cash" },
  { value: "BANK", label: "Bank" },
  { value: "UPI", label: "UPI" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "ONLINE", label: "Online" },
];

function formatPaymentOptionLabel(value, options) {
  const key = String(value ?? "")
    .trim()
    .toUpperCase();
  const found = options.find((o) => o.value === key);
  return found ? found.label : value || "—";
}

function formatPaymentTypeLabel(type) {
  return formatPaymentOptionLabel(type, PAYMENT_TYPE_OPTIONS);
}

function formatPaymentModeLabel(mode) {
  return formatPaymentOptionLabel(mode, PAYROLL_PAYMENT_MODE_OPTIONS);
}

function trimPaymentRef(ref) {
  const text = String(ref ?? "").trim();
  return text || null;
}

function PaymentSummaryMetrics({ netSalary, totalPaid, balanceDue }) {
  const items = [
    {
      label: "Net payable",
      value: formatINR(netSalary),
      className: "payroll-pay-metric payroll-pay-metric--accent",
    },
    {
      label: "Total paid",
      value: formatINR(totalPaid),
      className: "payroll-pay-metric payroll-pay-metric--paid",
    },
    {
      label: "Balance due",
      value: formatINR(balanceDue),
      className: "payroll-pay-metric payroll-pay-metric--due",
    },
  ];
  return (
    <div className="payroll-pay-metrics">
      {items.map((item) => (
        <div key={item.label} className={item.className}>
          <span className="payroll-pay-metric__label">{item.label}</span>
          <span className="payroll-pay-metric__value">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

const PAYMENT_TYPE_TONE = { ADVANCE: "advance", FINAL: "final" };

function payrollPaymentTypeClass(type) {
  return PAYMENT_TYPE_TONE[String(type || "").toUpperCase()] || "part";
}

function PayrollPayField({ label, children, className = "" }) {
  const blockClass = ["payroll-pay-row__field-block", className].filter(Boolean).join(" ");
  return (
    <div className={blockClass}>
      <span className="payroll-pay-row__label">{label}</span>
      {children}
    </div>
  );
}

function PayrollPaymentHistoryRow({ payment: p }) {
  const typeTone = payrollPaymentTypeClass(p.payment_type);
  const refText = trimPaymentRef(p.transaction_ref);

  return (
    <div className="payroll-pay-row" role="listitem">
      <div className="payroll-pay-row__body">
        <div className="payroll-pay-row__fields">
          <PayrollPayField label="Payment date">
            <span className="payroll-pay-row__date">{formatDateDMY(p.payment_date)}</span>
          </PayrollPayField>
          <PayrollPayField label="Payment type">
            <span className={`payroll-pay-type-pill payroll-pay-type-pill--${typeTone}`}>
              {formatPaymentTypeLabel(p.payment_type)}
            </span>
          </PayrollPayField>
          <PayrollPayField label="Payment mode">
            <span className="payroll-pay-mode-badge payroll-pay-mode-badge--row">
              {formatPaymentModeLabel(p.payment_mode)}
            </span>
          </PayrollPayField>
        </div>
        <PayrollPayField label="Paid amount" className="payroll-pay-row__field-block--amount">
          <span className="payroll-pay-row__amount">{formatINR(p.amount)}</span>
        </PayrollPayField>
      </div>
      <div className="payroll-pay-row__ref">
        <span className="payroll-pay-row__label">Reference</span>
        <span className={refText ? "payroll-pay-row__ref-value" : "payroll-pay-row__ref-empty"}>
          {refText || "—"}
        </span>
      </div>
    </div>
  );
}

function SalaryPaymentsSection({
  record,
  payments = [],
  summary,
  loading,
  error,
  isLocked,
}) {
  const netSalary = summary?.net_salary ?? numOrZero(record?.net_salary);
  const totalPaid = summary?.total_paid ?? numOrZero(record?.total_paid);
  const balanceDue = summary?.balance_due ?? numOrZero(record?.balance_due);

  const fullyPaid = balanceDue <= 0.009 && payments.length > 0;

  return (
    <Section title="Salary payments Status" compact variant="salaryComponents">
      <div className="payroll-pay-section-head">
        <span className="small fw-semibold" style={{ color: mdl.muted }}>
          {payments.length > 0
            ? `${payments.length} payment${payments.length === 1 ? "" : "s"} recorded`
            : "No payments yet"}
        </span>
      </div>

      <PaymentSummaryMetrics
        netSalary={netSalary}
        totalPaid={totalPaid}
        balanceDue={balanceDue}
      />

      <div className="mt-3">
        {loading ? (
          <p className="mb-0 small" style={{ color: mdl.muted, fontWeight: 600 }}>
            Loading payment history…
          </p>
        ) : null}
        {error ? (
          <p className="mb-2 small" style={{ color: "#b91c1c", fontWeight: 600 }}>
            {error}
          </p>
        ) : null}

        {payments.length > 0 ? (
          <div className="payroll-pay-history" role="list">
            {payments.map((p) => (
              <PayrollPaymentHistoryRow key={p.id} payment={p} />
            ))}
          </div>
        ) : !loading ? (
          <div className="payroll-pay-empty">
            No payments recorded yet. Use Record Payment below to add one.
          </div>
        ) : null}
      </div>

      {isLocked ? (
        <p className="mb-0 mt-2 small" style={{ color: mdl.muted }}>
          Unlock the record to record or edit payments (use Record Payment below).
        </p>
      ) : fullyPaid ? (
        <p className="mb-0 mt-2 small" style={{ color: "#047857", fontWeight: 600 }}>
          Salary fully paid. Use Record Payment below to view or edit payment entries.
        </p>
      ) : null}
    </Section>
  );
}

function PayrollSummarySection({ payrollAmounts, record, isEdit, derivedNet }) {
  const gross =
    payrollAmounts?.grossEarnings ?? numOrZero(record.gross_earnings);
  const totalDeductions =
    payrollAmounts?.totalDeductions ??
    Math.round(
      (numOrZero(payrollAmounts?.otherDeductions) +
        numOrZero(payrollAmounts?.lopAmount)) *
        100
    ) / 100;
  const net = isEdit
    ? derivedNet
    : numOrZero(record.net_salary ?? record.rounded_net);

  const items = [
    { label: "Gross earnings", value: gross, tone: "gross" },
    { label: "Total deductions", value: totalDeductions, tone: "deductions" },
    { label: "Net salary", value: net, tone: "netSalary" },
  ];

  return (
    <Section title="Salary summary" compact variant="calculations">
      <div className="d-flex flex-wrap align-items-stretch gap-2">
        {items.map((item) => (
          <div
            key={item.label}
            className="d-flex align-items-center gap-2 px-2 py-2"
            style={{
              borderRadius: mdl.radiusMd,
              ...entityBoxStyle(item.tone),
              flex: "1 1 160px",
              minWidth: 0,
            }}
          >
            <span
              style={{
                color: ENTITY_SURFACES[item.tone]?.label ?? mdl.muted,
                fontWeight: 700,
                fontSize: payrollType.fieldLabel,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
              }}
            >
              {item.label}
            </span>
            <span
              style={{
                color: mdl.text,
                fontWeight: 800,
                fontSize: 17,
                fontVariantNumeric: "tabular-nums",
                marginLeft: "auto",
              }}
            >
              {formatINR(item.value)}
            </span>
          </div>
        ))}
        {isEdit ? (
          <span
            className="align-self-center small px-1"
            style={{ color: mdl.muted, fontWeight: 600 }}
          >
            (preview)
          </span>
      ) : null}
      </div>
    </Section>
  );
}

/** Derived amounts from master basic, days, LOP, and salary type. */
function buildPayrollCalculationItems(
  record,
  form,
  isEdit,
  salaryComponentsBundle,
  amountsOverride = null
) {
  const basic = numOrZero(record.basic_salary);
  const workingDays = numOrZero(
    isEdit && form ? form.working_days : record.working_days
  );
  const payableDays = numOrZero(
    isEdit && form ? form.payable_days : record.payable_days
  );
  const amounts =
    amountsOverride ??
    computePayrollAmounts(record, form, isEdit, salaryComponentsBundle);
  const grossEarnings = amounts.grossEarnings;
  const otherDeductions = amounts.otherDeductions;
  const lopAmount = amounts.lopAmount;
  const totalDeductions = amounts.totalDeductions;
  const derivedNet = amounts.netSalary;
  const baseGross = amounts.baseGross;
  const componentEarnings = amounts.componentEarnings;
  const componentEarningsRaw =
    amounts.componentEarningsRaw ?? componentEarnings;
  const componentProrateFactor = amounts.componentProrateFactor ?? 1;

  const salaryTypeRaw =
    record.salary_type != null ? String(record.salary_type).trim() : "";
  const salaryType = salaryTypeRaw ? salaryTypeRaw.toUpperCase() : "—";

  const prorataBasis = normalizeProrataBasis(
    isEdit && form
      ? form.prorata_basis
      : record.salary_rules?.prorata_basis
  );
  const prorataLabel =
    PRORATA_BASIS_OPTIONS.find((o) => o.value === prorataBasis)?.label ?? "—";
  const divisorDays = resolveProrataDivisorDays(
    prorataBasis,
    record,
    workingDays
  );

  const perDayRate = computePerDayRate(record, form, isEdit);
  const lop = computePayrollLop(record, form, isEdit);

  const u = formatLopDays;
  const divisorLabel =
    prorataBasis === "month_30_days"
      ? "30"
      : prorataBasis === "calendar_days"
        ? `${divisorDays} calendar days`
        : "working days";

  const lopEquationLeft = [];
  const lopEquationRight = [];
  if (basic > 0 && salaryType === "DAILY") {
    lopEquationLeft.push(
      `Per day rate = Master basic (daily salary) = ${formatINR(perDayRate)}`
    );
  } else if (basic > 0 && divisorDays > 0) {
    lopEquationLeft.push(
      `Per day rate = Master basic ÷ ${divisorLabel} (${prorataLabel}) = ${formatINR(basic)} ÷ ${divisorDays} = ${formatINR(perDayRate)}`
    );
  }
  lopEquationLeft.push(
    "LOP (total) = Leave & absent LOP + Late LOP + Half day LOP",
    `Leave & absent LOP = max(0, Leave − Paid leave) + Absent = max(0, ${u(lop.leave)} − ${u(lop.paidLeave)}) + ${u(lop.absent)} = ${u(lop.leaveAbsentLop)}`,
    `Late LOP = floor(Late ÷ Late per LOP) = floor(${u(lop.lateDay)} ÷ ${u(lop.latePerLop)}) = ${u(lop.lateLop)}  (every ${u(lop.latePerLop)} late → 1 LOP day)`,
    `Half day LOP = Half day × Half day weight = ${u(lop.halfDay)} × ${u(lop.halfDayWeight)} = ${u(lop.halfDayLop)}`,
    `LOP (total) = ${u(lop.leaveAbsentLop)} + ${u(lop.lateLop)} + ${u(lop.halfDayLop)} = ${u(lop.totalLop)} day(s)`
  );
  if (perDayRate > 0) {
    lopEquationRight.push(
      `LOP amount = Total LOP × Per day rate = ${u(lop.totalLop)} × ${formatINR(perDayRate)} = ${formatINR(lopAmount)}`
    );
  }
  if (
    salaryType === "MONTHLY" &&
    workingDays > 0 &&
    payableDays >= 0 &&
    payableDays < workingDays
  ) {
    lopEquationRight.push(
      `Basic (prorata) = Master basic × Payable days ÷ Working days = ${formatINR(basic)} × ${formatNum(payableDays)} ÷ ${formatNum(workingDays)} = ${formatINR(baseGross)}`
    );
  }
  const grossComponentsPart =
    componentProrateFactor < 1 && componentEarningsRaw > 0
      ? `${formatINR(componentEarningsRaw)} × ${formatNum(payableDays)} ÷ ${formatNum(workingDays)}`
      : formatINR(componentEarnings);
  lopEquationRight.push(
    `Gross earnings = Basic (prorata) + Earning components = ${formatINR(baseGross)} + ${grossComponentsPart} = ${formatINR(grossEarnings)}`,
    `Other deductions = Sum of deduction components = ${formatINR(otherDeductions)}`,
    `Net (calc.) = Gross earnings − LOP amount − Other deductions = ${formatINR(grossEarnings)} − ${formatINR(lopAmount)} − ${formatINR(otherDeductions)} = ${formatINR(derivedNet)}`
  );

  const primaryItems = [
    { label: "Salary type", value: salaryType, tone: "basic" },
    { label: "Master basic", value: formatINR(basic), tone: "basic" },
    { label: "Prorata Basis", value: prorataLabel, tone: "payMonth" },
    {
      label: "Working days",
      value: formatNum(workingDays),
      tone: "workingDays",
    },
    { label: "Payable days", value: formatNum(payableDays), tone: "payableDays" },
    { label: "Per day rate", value: formatINR(perDayRate), tone: "payableDays" },
  ];

  const summaryRowItems = [
    { label: "Gross earnings", value: formatINR(grossEarnings), tone: "gross" },
    { label: "Total LOP days", value: formatLopDays(lop.totalLop), tone: "deductions" },
    { label: "LOP amount", value: formatINR(lopAmount), tone: "deductions" },
    { label: "Other deductions", value: formatINR(otherDeductions), tone: "deductions" },
    { label: "Total deductions", value: formatINR(totalDeductions), tone: "deductions" },
    { label: "Net (calc.)", value: formatINR(derivedNet), tone: "netSalary" },
  ];

  return {
    primaryItems,
    summaryRowItems,
    lopEquationLeft,
    lopEquationRight,
  };
}

const LopEquationLines = React.memo(function LopEquationLines({ lines }) {
  return lines.map((line) => (
    <div
      key={line}
      style={{ marginBottom: 4, fontVariantNumeric: "tabular-nums" }}
    >
      {line}
    </div>
  ));
});

const CalculationChipRow = React.memo(function CalculationChipRow({ items }) {
  const colClass =
    items.length >= 6
      ? "row g-1 flex-nowrap payroll-calc-summary-row"
      : items.length <= 3
        ? "row g-1 row-cols-1 row-cols-sm-3"
        : "row g-1 row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-6 row-cols-xl-8";
  const rowStyle =
    items.length >= 6
      ? { overflowX: "auto", WebkitOverflowScrolling: "touch" }
      : undefined;
  return (
    <div className={colClass} style={rowStyle}>
      {items.map((item) => (
        <HorizontalStatChip
          key={item.label}
          label={item.label}
          value={item.value}
          tone={item.tone}
          centered
        />
      ))}
    </div>
  );
});

function partitionSalaryComponents(bundle) {
  if (!bundle) {
    return {
      addedEarnings: [],
      addedDeductions: [],
      masterEarnings: [],
      masterDeductions: [],
      totalEarnings: 0,
      totalDeductions: 0,
    };
  }
  const staff = bundle.staff_components || [];
  const master = bundle.component_master || [];
  const addedEarnings = staff.filter(
    (s) => String(s.component_type).toUpperCase() === "EARNING"
  );
  const addedDeductions = staff.filter(
    (s) => String(s.component_type).toUpperCase() === "DEDUCTION"
  );
  const earnCodes = new Set(addedEarnings.map((s) => String(s.component_code)));
  const dedCodes = new Set(addedDeductions.map((s) => String(s.component_code)));
  const masterEarnings = master.filter(
    (m) =>
      String(m.component_type).toUpperCase() === "EARNING" &&
      !earnCodes.has(String(m.component_code))
  );
  const masterDeductions = master.filter(
    (m) =>
      String(m.component_type).toUpperCase() === "DEDUCTION" &&
      !dedCodes.has(String(m.component_code))
  );
  const totalEarnings = addedEarnings.reduce(
    (sum, s) => sum + numOrZero(s.amount),
    0
  );
  const totalDeductions = addedDeductions.reduce(
    (sum, s) => sum + numOrZero(s.amount),
    0
  );
  return {
    addedEarnings,
    addedDeductions,
    masterEarnings,
    masterDeductions,
    totalEarnings,
    totalDeductions,
  };
}

function componentDisplayName(item) {
  return item.component_name || item.component_code || "—";
}

function SalaryComponentRow({
  item,
  tone,
  isEdit,
  disabled,
  onUpdateAmount,
  onRemove,
  rowBusy,
}) {
  const [draftAmount, setDraftAmount] = useState(String(numOrZero(item.amount)));

  useEffect(() => {
    setDraftAmount(String(numOrZero(item.amount)));
  }, [item.amount, item.id]);

  const label = componentDisplayName(item);
  const calcHint = item.calc_source ? ` · ${item.calc_source}` : "";

  return (
    <div
      className="d-flex align-items-center gap-2 py-1 px-2 mb-1"
        style={{
        borderRadius: mdl.radiusSm,
        ...entityBoxStyle(tone),
        opacity: rowBusy ? 0.65 : 1,
      }}
      title={`${label}${calcHint}`}
    >
      <span
        className="flex-grow-1 text-truncate"
        style={{
          fontWeight: 600,
          fontSize: payrollType.fieldInputCompact,
          color: mdl.text,
        }}
      >
        {label}
      </span>
      {isEdit && !disabled ? (
        <Form.Control
          type="number"
          min={0}
          step="0.01"
          value={draftAmount}
          disabled={rowBusy}
          onChange={(e) => setDraftAmount(e.target.value)}
          onBlur={() => {
            const next = numOrZero(draftAmount);
            if (next !== numOrZero(item.amount)) {
              onUpdateAmount(item.id, next);
            }
          }}
          style={{
            width: 96,
            ...formInputStyle(rowBusy, tone),
            padding: "4px 8px",
            fontSize: payrollType.fieldInputCompact,
            textAlign: "right",
          }}
        />
      ) : (
        <span
          style={{
            fontWeight: 800,
            fontVariantNumeric: "tabular-nums",
            fontSize: payrollType.chipValue,
            flexShrink: 0,
          }}
        >
          {formatINR(item.amount)}
        </span>
      )}
      {isEdit && !disabled ? (
        <Button
          variant="link"
          size="sm"
          className="p-0 text-danger flex-shrink-0"
          disabled={rowBusy}
          onClick={() => onRemove(item.id)}
          title="Remove component"
          style={{ fontSize: 18, lineHeight: 1, textDecoration: "none" }}
        >
          ×
        </Button>
      ) : null}
    </div>
  );
}

function SalaryComponentsHalfPanel({
  title,
  tone,
  componentType,
  addedItems,
  masterOptions,
  total,
  isEdit,
  disabled,
  onAdd,
  onUpdateAmount,
  onRemove,
  actionBusy,
}) {
  const [pickValue, setPickValue] = useState("");
  const labelColor = ENTITY_SURFACES[tone]?.label || mdl.accent;

  const handlePick = (code) => {
    if (!code) return;
    onAdd(code, componentType);
    setPickValue("");
  };

  return (
    <div
      className="h-100 d-flex flex-column"
      style={{
        borderRadius: mdl.radiusMd,
        border: `1px solid ${ENTITY_SURFACES[tone]?.border || mdl.border}`,
        background: ENTITY_SURFACES[tone]?.bg || mdl.surfaceMuted,
        padding: "8px 10px",
        minHeight: 120,
      }}
    >
      <div
        className="mb-2"
        style={{
          fontSize: payrollType.fieldLabel,
          fontWeight: 750,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: labelColor,
        }}
      >
        {title}
      </div>
      {addedItems.length === 0 ? (
        <div className="small mb-2" style={{ color: mdl.muted, fontWeight: 600 }}>
          No components added yet.
        </div>
      ) : (
        <div className="flex-grow-1 mb-1" style={{ maxHeight: 220, overflowY: "auto" }}>
          {addedItems.map((item) => (
            <SalaryComponentRow
              key={item.id}
              item={item}
              tone={tone}
              isEdit={isEdit}
              disabled={disabled}
              onUpdateAmount={onUpdateAmount}
              onRemove={onRemove}
              rowBusy={actionBusy === item.id}
            />
          ))}
        </div>
      )}
      <div
        className="d-flex justify-content-between align-items-center gap-2 py-1 px-1 mt-auto"
        style={{ borderTop: `1px solid ${mdl.borderHairline}` }}
      >
        <span style={{ fontWeight: 700, fontSize: payrollType.fieldLabel, color: labelColor }}>
          Total
        </span>
        <span style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
          {formatINR(total)}
        </span>
      </div>
      {isEdit && !disabled ? (
        <div className="mt-2">
          {masterOptions.length > 0 ? (
            <Form.Select
              value={pickValue}
              disabled={!!actionBusy}
              onChange={(e) => handlePick(e.target.value)}
              style={{
                ...formInputStyle(!!actionBusy, tone),
                fontSize: payrollType.fieldInputCompact,
                fontWeight: 600,
              }}
            >
              <option value="">Add component…</option>
              {masterOptions.map((m) => (
                <option key={m.component_code} value={m.component_code}>
                  {m.component_name || m.component_code}
                </option>
              ))}
            </Form.Select>
          ) : (
            <div className="small" style={{ color: mdl.muted, fontWeight: 600 }}>
              All {title.toLowerCase()} from master are already added.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function SalaryComponentsSection({
  bundle,
  loading,
  error,
  actionError,
  isEdit,
  disabled,
  onAdd,
  onUpdateAmount,
  onRemove,
  actionBusy,
}) {
  const {
    addedEarnings,
    addedDeductions,
    masterEarnings,
    masterDeductions,
    totalEarnings,
    totalDeductions,
  } = partitionSalaryComponents(bundle);

  return (
    <Section title="Salary components" compact variant="salaryComponents">
      {loading ? (
        <div
          className="text-center py-2"
          style={{ color: mdl.muted, fontSize: 13, fontWeight: 600 }}
        >
          Loading components…
        </div>
      ) : error ? (
        <div className="small py-1" style={{ color: "#b91c1c", fontWeight: 600 }}>
          {error}
        </div>
      ) : (
        <>
          {actionError ? (
            <div className="small py-1 mb-2" style={{ color: "#b91c1c", fontWeight: 600 }}>
              {actionError}
            </div>
          ) : null}
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <SalaryComponentsHalfPanel
                title="Earnings"
                tone="allowances"
                componentType="EARNING"
                addedItems={addedEarnings}
                masterOptions={masterEarnings}
                total={totalEarnings}
                isEdit={isEdit}
                disabled={disabled}
                onAdd={onAdd}
                onUpdateAmount={onUpdateAmount}
                onRemove={onRemove}
                actionBusy={actionBusy}
              />
            </div>
            <div className="col-12 col-md-6">
              <SalaryComponentsHalfPanel
                title="Deductions"
                tone="deductions"
                componentType="DEDUCTION"
                addedItems={addedDeductions}
                masterOptions={masterDeductions}
                total={totalDeductions}
                isEdit={isEdit}
                disabled={disabled}
                onAdd={onAdd}
                onUpdateAmount={onUpdateAmount}
                onRemove={onRemove}
                actionBusy={actionBusy}
              />
            </div>
          </div>
        </>
      )}
    </Section>
  );
}

const CalculationsSection = React.memo(function CalculationsSection({
  calculationDisplay,
}) {
  if (!calculationDisplay) return null;
  const {
    primaryItems,
    summaryRowItems,
    lopEquationLeft,
    lopEquationRight,
  } = calculationDisplay;
  return (
    <Section title="Calculations" compact variant="calculations">
      <CalculationChipRow items={primaryItems} />
      <div className="mt-1">
        <CalculationChipRow items={summaryRowItems} />
      </div>
      <div
        className="mt-2 pt-2"
        style={{
          borderTop: `1px solid ${mdl.borderHairline}`,
          fontFamily: mdl.font,
          fontSize: 12,
          lineHeight: 1.55,
          color: mdl.textSoft,
        }}
      >
        <div
          style={{
            fontWeight: 750,
            fontSize: payrollType.sectionTitle,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: mdl.accent,
            marginBottom: 6,
          }}
        >
          LOP (LOSS OF PAY) equation
        </div>
        <div className="row g-2">
          <div className="col-12 col-md-6">
            <div
              className="h-100 px-2 py-1"
              style={{
                borderRadius: mdl.radiusSm,
                border: `1px solid ${mdl.borderHairline}`,
                background: "rgba(248, 250, 252, 0.65)",
              }}
            >
              <div
                className="mb-1"
                style={{
                  fontWeight: 700,
                  fontSize: payrollType.chipLabel,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: mdl.muted,
                }}
              >
                LOP days
              </div>
              <LopEquationLines lines={lopEquationLeft} />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div
              className="h-100 px-2 py-1"
              style={{
                borderRadius: mdl.radiusSm,
                border: `1px solid ${mdl.borderHairline}`,
                background: "rgba(248, 250, 252, 0.65)",
              }}
            >
              <div
                className="mb-1"
                style={{
                  fontWeight: 700,
                  fontSize: payrollType.chipLabel,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: mdl.muted,
                }}
              >
                LOP amount & net
              </div>
              <LopEquationLines lines={lopEquationRight} />
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
});

function Section({ title, children, denseTop, compact, variant }) {
  const isTintedSection =
    variant === "daysAttendance" ||
    variant === "salaryRules" ||
    variant === "calculations" ||
    variant === "salaryComponents";
  return (
    <div className={compact ? "mb-1" : "mb-2"} style={{ fontFamily: mdl.font }}>
      <div
        className={`d-flex align-items-center gap-2 ${denseTop || compact ? "mb-0" : "mb-1"}`}
        style={{
          fontSize: payrollType.sectionTitle,
          fontWeight: 750,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: mdl.accent,
        }}
      >
        {title}
      </div>
      <div
        className="payroll-mdl-section-body"
        style={{
          background: isTintedSection
            ? daysAttendanceSectionBody.background
            : mdl.surface,
          borderRadius: mdl.radiusLg,
          padding: compact ? "6px 8px" : denseTop ? "6px 10px 8px" : "8px 12px",
          border: isTintedSection
            ? daysAttendanceSectionBody.border
            : `1px solid ${mdl.border}`,
          boxShadow: isTintedSection
            ? daysAttendanceSectionBody.boxShadow
            : mdl.shadowCard,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function formInputStyle(disabled, entityTone) {
  const e = entityTone ? ENTITY_SURFACES[entityTone] : null;
  const borderColor = e?.border ?? "rgba(71, 85, 105, 0.55)";
  return {
    borderRadius: mdl.radiusMd,
    border: `1.5px solid ${borderColor}`,
    background: disabled ? "#f1f5f9" : "#ffffff",
    fontSize: payrollType.fieldInput,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    padding: "6px 10px",
    fontFamily: mdl.font,
    color: disabled ? "#64748b" : e?.value ?? e?.label ?? mdl.text,
    boxShadow: disabled ? "none" : "inset 0 1px 2px rgba(15, 23, 42, 0.04)",
  };
}

function FieldNum({
  label,
  value,
  onChange,
  disabled,
  step = "0.01",
  min = "0",
  entityTone,
  compact = false,
}) {
  const e = entityTone ? ENTITY_SURFACES[entityTone] : null;
  const inputStyle = formInputStyle(disabled, entityTone);
  const stepNum = parseFloat(step) || 0.01;
  const minNum = parseFloat(min) || 0;
  const wholeStep = stepNum >= 1 && Number.isInteger(stepNum);

  const normalizeValue = (raw) => {
    if (raw === "" || raw === null || raw === undefined) {
      return wholeStep ? 0 : "";
    }
    let n = Number(raw);
    if (Number.isNaN(n)) return wholeStep ? 0 : raw;
    if (wholeStep) n = Math.round(n);
    if (n < minNum) n = minNum;
    return n;
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange(wholeStep ? 0 : "");
      return;
    }
    onChange(normalizeValue(raw));
  };

  const inner = (
    <Form.Group className="mb-0">
      <Form.Label
        className={`d-block ${compact ? "mb-0" : "mb-1"}`}
        style={{
          color: e?.label ?? mdl.muted,
          fontWeight: 600,
          fontSize: compact ? payrollType.fieldLabelCompact : payrollType.fieldLabel,
          letterSpacing: "0.02em",
          fontFamily: mdl.font,
          lineHeight: 1.25,
        }}
      >
        {label}
      </Form.Label>
      <Form.Control
        type="number"
        step={step}
        min={min}
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        style={{
          ...inputStyle,
          ...(compact
            ? { padding: "4px 8px", fontSize: payrollType.fieldInputCompact }
            : null),
        }}
      />
    </Form.Group>
  );
  if (!e) {
    return inner;
  }
  return (
    <div
      style={{
        padding: "6px 8px",
        borderRadius: mdl.radiusMd,
        ...entityBoxStyle(entityTone),
      }}
    >
      {inner}
    </div>
  );
}

function FieldSelect({
  label,
  value,
  options,
  onChange,
  disabled,
  entityTone,
  centered = false,
}) {
  const e = entityTone ? ENTITY_SURFACES[entityTone] : null;
  const selectStyle = formInputStyle(disabled, entityTone);
  const inner = (
    <Form.Group className={`mb-0${centered ? " text-center" : ""}`}>
      <Form.Label
        className={`mb-1 d-block${centered ? " text-center" : ""}`}
        style={{
          color: e?.label ?? mdl.muted,
          fontWeight: 600,
          fontSize: payrollType.fieldLabel,
          letterSpacing: "0.02em",
          fontFamily: mdl.font,
        }}
      >
        {label}
      </Form.Label>
      <Form.Select
        value={value}
        onChange={(ev) => onChange && onChange(ev.target.value)}
        disabled={disabled}
        style={{
          ...selectStyle,
          ...(centered ? { textAlign: "center", textAlignLast: "center" } : null),
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
  if (!e) {
    return inner;
  }
  return (
    <div
      style={{
        padding: "6px 8px",
        borderRadius: mdl.radiusMd,
        ...(centered ? { textAlign: "center" } : null),
        ...entityBoxStyle(entityTone),
      }}
    >
      {inner}
    </div>
  );
}

export function getPayrollModalStyles() {
  return `
        .payroll-mdl .payroll-mdl-section-body > .payroll-mdl-row:last-child {
          border-bottom: none;
          padding-bottom: 2px;
        }
        .payroll-mdl .payroll-mdl-section-body .payroll-mdl-row:last-child {
          margin-bottom: 0 !important;
        }
        .payroll-mdl .form-control,
        .payroll-mdl .form-select {
          color: ${mdl.text} !important;
          border: 1.5px solid rgba(71, 85, 105, 0.55) !important;
          background: #ffffff !important;
        }
        .payroll-mdl .form-control::placeholder {
          color: #94a3b8;
          opacity: 1;
        }
        .payroll-mdl .form-control:disabled,
        .payroll-mdl .form-select:disabled {
          color: ${mdl.muted} !important;
          border-color: rgba(148, 163, 184, 0.5) !important;
          background: #f1f5f9 !important;
        }
        .payroll-mdl .form-control:focus,
        .payroll-mdl .form-select:focus {
          color: ${mdl.text} !important;
          border-color: ${COLORS.primary_color_shade6 || "#0288d1"} !important;
          box-shadow: 0 0 0 3px ${mdl.ringFocus} !important;
          background: #ffffff !important;
        }
        .payroll-mdl .form-check-input {
          width: 1.5rem;
          height: 1.5rem;
          min-width: 1.5rem;
          border-radius: 8px;
        }
        .payroll-mdl .form-check-input:checked {
          background-color: ${COLORS.primary_color_shade6 || "#0288d1"};
          border-color: ${COLORS.primary_color_shade6 || "#0288d1"};
        }
        .payroll-mdl .payroll-mdl-lock .form-check-input {
          border-color: rgba(217, 119, 6, 0.55);
        }
        .payroll-mdl .payroll-mdl-lock .form-check-input:focus {
          border-color: #d97706;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.32);
        }
        .payroll-mdl .payroll-mdl-lock .form-check-input:checked {
          background-color: #d97706;
          border-color: #b45309;
        }
        .payroll-mdl .modal-header .btn-close {
          filter: brightness(0) invert(1);
          opacity: 0.88;
        }
        @keyframes payroll-mdl-spin {
          to { transform: rotate(360deg); }
        }
        .payroll-mdl .payroll-calc-summary-row > .col {
          flex: 1 0 0;
          min-width: 104px;
        }
        .payroll-mdl-spinner {
          animation: payroll-mdl-spin 0.65s linear infinite;
        }
        .modal-payroll-mdl-wide.modal-dialog {
          max-width: min(1200px, 96vw);
          width: min(1200px, 96vw);
        }
        .custom-modal-content.payroll-mdl.modal-content {
          padding: 0 !important;
          border: 1px solid ${mdl.border} !important;
          border-radius: ${mdl.radiusXl}px !important;
          overflow: hidden !important;
          background: #ffffff !important;
          box-shadow: ${mdl.shadowFloat} !important;
          outline: none;
        }
        .custom-modal-content.payroll-mdl.modal-content > div:first-child {
          border-radius: inherit;
        }
      `;
}

export {
  STATUS_OPTIONS,
  PRORATA_BASIS_OPTIONS,
  PAYROLL_ATTENDANCE_FIELD_KEYS,
  mdl,
  payrollType,
  headerMetaGrid,
  headerMetaPill,
  headerMetaLabel,
  numOrZero,
  formatINR,
  formatYearMonth,
  formatDateDMY,
  formatNum,
  buildFormFromRecord,
  normalizeProrataBasis,
  Section,
  Row,
  DaysAttendanceSection,
  SalaryRulesSection,
  CalculationsSection,
  SalaryComponentsSection,
  PayrollSummarySection,
  SalaryPaymentsSection,
  PAYMENT_TYPE_OPTIONS,
  PAYROLL_PAYMENT_MODE_OPTIONS,
  PaymentSummaryMetrics,
  formatPaymentModeLabel,
  PayrollHeaderChipBand,
  StaffPayrollHeaderChip,
  HeaderLockAction,
  HeaderSyncAttendanceAction,
  staffPayrollViewCells,
  computePayrollAmounts,
  buildPayrollCalculationItems,
  computeLopAmount,
  FieldNum,
  FieldSelect,
  formInputStyle,
  entityBoxStyle,
};

